import React, { useEffect, useState } from 'react';
import TradingChart from '../../components/Chart/TradingChart';

const Dashboard: React.FC = () => {
  const [chartData, setChartData] = useState<any[]>([]);

  useEffect(() => {
    const mockData = generateMockData();
    setChartData(mockData);
  }, []);

  const generateMockData = () => {
    const data = [];
    const now = Math.floor(Date.now() / 1000);
    let price = 50000;

    for (let i = 100; i >= 0; i--) {
      const timestamp = now - i * 3600;
      const change = (Math.random() - 0.5) * 500;
      price += change;

      const open = price;
      const close = price + (Math.random() - 0.5) * 300;
      const high = Math.max(open, close) + Math.random() * 200;
      const low = Math.min(open, close) - Math.random() * 200;

      data.push({ time: timestamp, open, high, low, close });
    }
    return data;
  };

  const totalPnl = 2543.21;
  const totalPnlPercent = 12.5;
  const isPositive = totalPnl >= 0;

  return (
    <div className="max-w-[1400px] mx-auto p-5">
      {/* Hero — Total P&L */}
      <div className="mb-8">
        <p className="text-viz-text-secondary text-sm mb-1">Total Portfolio Value</p>
        <div className="flex items-baseline gap-3">
          <h1 className={`text-4xl font-bold font-mono ${isPositive ? 'text-viz-success' : 'text-viz-error'}`}>
            {isPositive ? '+' : '-'}${Math.abs(totalPnl).toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </h1>
          <span className={`text-sm font-medium px-2 py-0.5 rounded-full ${
            isPositive
              ? 'bg-viz-success-bg text-viz-success'
              : 'bg-viz-error-bg text-viz-error'
          }`}>
            {isPositive ? '+' : ''}{totalPnlPercent}%
          </span>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="glass-card p-5">
          <p className="text-viz-text-secondary text-xs uppercase tracking-wider mb-2">Portfolio Value</p>
          <p className="text-2xl font-bold font-mono text-viz-text-primary">$52,543</p>
          <p className="text-xs text-viz-success mt-1">+12.5% all time</p>
        </div>

        <div className="glass-card p-5">
          <p className="text-viz-text-secondary text-xs uppercase tracking-wider mb-2">Win Rate</p>
          <p className="text-2xl font-bold font-mono text-viz-text-primary">68%</p>
          <p className="text-xs text-viz-success mt-1">+5% this week</p>
        </div>

        <div className="glass-card p-5">
          <p className="text-viz-text-secondary text-xs uppercase tracking-wider mb-2">Active Bots</p>
          <div className="flex items-center gap-2">
            <p className="text-2xl font-bold font-mono text-viz-text-primary">3</p>
            <span className="w-2 h-2 rounded-full bg-viz-success animate-pulse"></span>
          </div>
          <p className="text-xs text-viz-text-secondary mt-1">All running</p>
        </div>

        <div className="glass-card p-5">
          <p className="text-viz-text-secondary text-xs uppercase tracking-wider mb-2">Total Trades</p>
          <p className="text-2xl font-bold font-mono text-viz-text-primary">142</p>
          <p className="text-xs text-viz-text-secondary mt-1">This month</p>
        </div>
      </div>

      {/* Chart Section */}
      <div className="glass-card p-5 mb-6">
        <h2 className="text-lg font-semibold text-viz-text-primary mb-4">BTCUSDT Market Overview</h2>
        <TradingChart symbol="BTCUSDT" data={chartData} height={500} />
      </div>

      {/* Active Strategies */}
      <div className="glass-card p-5 mb-6">
        <h2 className="text-lg font-semibold text-viz-text-primary mb-4">Active Strategies</h2>
        <div className="flex flex-col gap-3">
          {[
            { name: 'RSI Scalping Strategy', symbol: 'BTCUSDT • 1h', pnl: 845.32, status: 'Running' },
            { name: 'Moving Average Crossover', symbol: 'ETHUSDT • 4h', pnl: 1254.89, status: 'Running' },
            { name: 'Breakout Strategy', symbol: 'BNBUSDT • 1d', pnl: 443.00, status: 'Running' },
          ].map((s, i) => (
            <div key={i} className="flex items-center justify-between p-4 rounded-lg bg-viz-bg-primary/50 border border-viz-border hover:border-viz-border-hover transition-colors">
              <div>
                <h3 className="text-sm font-medium text-viz-text-primary">{s.name}</h3>
                <p className="text-xs text-viz-text-secondary mt-0.5">{s.symbol}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-sm font-semibold text-viz-success">
                  +${s.pnl.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
                <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-viz-success-bg text-viz-success">
                  {s.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Open Positions */}
      <div className="glass-card p-5">
        <h2 className="text-lg font-semibold text-viz-text-primary mb-4">Open Positions</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-viz-border">
                <th className="text-left text-xs text-viz-text-secondary font-medium py-3 px-3">Symbol</th>
                <th className="text-left text-xs text-viz-text-secondary font-medium py-3 px-3">Side</th>
                <th className="text-left text-xs text-viz-text-secondary font-medium py-3 px-3">Size</th>
                <th className="text-left text-xs text-viz-text-secondary font-medium py-3 px-3">Entry</th>
                <th className="text-left text-xs text-viz-text-secondary font-medium py-3 px-3">Current</th>
                <th className="text-left text-xs text-viz-text-secondary font-medium py-3 px-3">P&L</th>
              </tr>
            </thead>
            <tbody>
              {[
                { symbol: 'BTCUSDT', side: 'LONG', size: '0.5', entry: '$49,850', current: '$50,250', pnl: 200.00 },
                { symbol: 'ETHUSDT', side: 'LONG', size: '5.0', entry: '$2,450', current: '$2,520', pnl: 350.00 },
              ].map((p, i) => (
                <tr key={i} className="border-b border-viz-border/50 hover:bg-viz-bg-tertiary/30 transition-colors">
                  <td className="py-3 px-3 text-sm font-medium text-viz-text-primary">{p.symbol}</td>
                  <td className={`py-3 px-3 text-sm font-semibold ${p.side === 'LONG' ? 'text-viz-success' : 'text-viz-error'}`}>{p.side}</td>
                  <td className="py-3 px-3 text-sm font-mono text-viz-text-primary">{p.size}</td>
                  <td className="py-3 px-3 text-sm font-mono text-viz-text-secondary">{p.entry}</td>
                  <td className="py-3 px-3 text-sm font-mono text-viz-text-primary">{p.current}</td>
                  <td className={`py-3 px-3 text-sm font-mono font-semibold ${p.pnl >= 0 ? 'text-viz-success' : 'text-viz-error'}`}>
                    {p.pnl >= 0 ? '+' : '-'}${Math.abs(p.pnl).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
